/**
 * Cookie utility functions for managing browser cookies
 */

/**
 * Get a URL query parameter by name
 * @param {string} name - The parameter name to retrieve
 * @returns {string|null} - The parameter value or null if not found
 */
export function getUrlParameter(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * Get the root domain from the current hostname
 * Works correctly across localhost, staging and production.
 * @returns {string} - Cookie domain with leading dot or empty string for localhost/IPs
 */
function getRootDomain() {
  const hostname = window.location.hostname;

  // Local/dev or IPs: don't set domain attribute at all
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return '';
  }

  // If hostname ends with yummi.co.il, force shared root domain across all subdomains
  if (hostname.endsWith('yummi.co.il')) {
    return '.yummi.co.il';
  }

  // Fallback: use full hostname as cookie domain (e.g. .preview-env.example.com)
  return '.' + hostname;
}

/**
 * Set a cookie with specified name, value, and expiration
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration
 */
export function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  const domain = getRootDomain();
  const isSecure = window.location.protocol === 'https:';
  
  let cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  
  // Add domain attribute if we have a root domain
  if (domain) {
    cookieString += `;domain=${domain}`;
  }
  
  // Add Secure flag if HTTPS
  if (isSecure) {
    cookieString += ';Secure';
  }
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param {string} name - The cookie name to retrieve
 * @returns {string|null} - The cookie value or null if not found
 */
export function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  
  return null;
}
